import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import Product from '@/creditflowresearch/domain/Product/Product';
import Segment from '@/creditflowresearch/domain/Segment';
import { ProductAbbreviations, ProductType } from '@/creditflowresearch/domain/Product/types/ProductType';
import { CORPORATE_SEGMENTS } from '@/creditflowresearch/domain/Segment/constants';
import {
  HYC_PRODUCT_ID,
  IGC_PRODUCT_ID,
  HYC_ABBREVIATION,
  IGC_ABBREVIATION,
  HYC_TYPE,
  IGC_TYPE,
} from '@/creditflowresearch/domain/Product/constants';
import {
  US_MARKET_ID,
  EUROPEAN_MARKET_ID,
  APAC_MARKET_ID,
} from '@/creditflowresearch/domain/Market/constants';

abstract class CorporateProduct extends Product {
  static readonly CORPORATE_PRODUCT_IDS = [
    HYC_PRODUCT_ID,
    IGC_PRODUCT_ID,
  ];

  static readonly CORPORATE_TYPES: ProductType[] = [
    HYC_TYPE,
    IGC_TYPE,
  ];

  static readonly ABBREVIATION: ProductAbbreviations = ProductAbbreviations.CORPORATE;

  getDefaultProductId(accessibleProductIds: string[] = []): string {
    if (accessibleProductIds.length > 0) {
      const accessibleSet = new Set(accessibleProductIds);
      const accessibleCorporate = CorporateProduct.CORPORATE_PRODUCT_IDS.find((id) =>
        accessibleSet.has(id),
      );
      if (accessibleCorporate) {
        return accessibleCorporate;
      }
    }

    return this.id;
  }

  static getProductsAbbreviations(): ProductAbbreviations[] {
    return [HYC_ABBREVIATION, IGC_ABBREVIATION];
  }

  getDefaultForContext(params: {
    accessibleProducts: Product[],
    hasAccess: boolean,
    allProductsList: IProduct[],
  }): Product | null {
    return CorporateProduct.getDefault(params);
  }

  getMarketIds(): string[] {
    return [
      US_MARKET_ID,
      EUROPEAN_MARKET_ID,
      APAC_MARKET_ID,
    ];
  }

  getSegments(): Segment[] {
    return CORPORATE_SEGMENTS.map((type) => Segment.from(type)).filter(
      (segment): segment is Segment => segment !== null,
    );
  }

  static getDefault(params: {
    accessibleProducts: Product[],
    hasAccess: boolean,
  }): Product | null {
    if (params.hasAccess && params.accessibleProducts.length > 0) {
      const accessibleCorporate = params.accessibleProducts.find((product) =>
        product.isOfType(HYC_TYPE) || product.isOfType(IGC_TYPE),
      );
      if (accessibleCorporate) {
        return accessibleCorporate;
      }
    }

    return Product.from(HYC_PRODUCT_ID);
  }
}

export default CorporateProduct;
